import React from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import UnderConstructionBanner from "../components/UnderConstructionBanner";
import {
  BookOpen, FileText, LineChart, Users, Receipt, Landmark, RefreshCcw,
  ScrollText, Building2, Upload, Sparkles, Lock, HeartPulse,
} from "lucide-react";

/**
 * iter150 — Accounting Pro (CA) module hub.
 *
 * Shell only. Every sub-page renders `<UnderConstructionBanner />` until we
 * flesh out the backend. Sidebar sub-nav is baked in here so users see the
 * whole scope at a glance and can preview each area even while empty.
 */
const SUBSECTIONS = [
  { href: "/accounting/chart-of-accounts", label: "Chart of Accounts", icon: BookOpen,
    desc: "Set up your ledger accounts. Canadian standard small-business template pre-loaded." },
  { href: "/accounting/journal-entries", label: "Journal Entries", icon: FileText,
    desc: "Draft, review and post journal entries. Toggle auto-post per your workflow." },
  { href: "/accounting/general-ledger", label: "General Ledger", icon: ScrollText,
    desc: "Drill into any account. Sub-ledger detail with running balances." },
  { href: "/accounting/reports", label: "Financial Reports", icon: LineChart,
    desc: "P&L, Balance Sheet, Trial Balance — date-range filtered." },
  { href: "/accounting/vendors", label: "Vendors & Suppliers", icon: Users,
    desc: "Supplier records: address, contacts, terms, preferred payment, default GL." },
  { href: "/accounting/vendor-bills", label: "Vendor Bills (AP)", icon: Receipt,
    desc: "Vendor invoices. Due date auto-fills from supplier terms. GL-linked line items." },
  { href: "/accounting/cheques", label: "Cheques", icon: FileText,
    desc: "Two-stub cheque printing. Each stub itemises every invoice being paid." },
  { href: "/accounting/bank-accounts", label: "Bank Accounts", icon: Landmark,
    desc: "Multiple accounts per business, per branch, per store. CAD-focused." },
  { href: "/accounting/reconciliation", label: "Bank Reconciliation", icon: RefreshCcw,
    desc: "Import statements from RBC/TD/Scotia/BMO/CIBC + reconcile against your books." },
  { href: "/accounting/branches", label: "Franchise & Branches", icon: Building2,
    desc: "Multi-store books. Each branch has its own set of books that rolls up to head office." },
  { href: "/accounting/health-score", label: "Accounting Health Score", icon: HeartPulse,
    desc: "Live gauge — flags unbalanced ledgers, unmatched bank transactions, overdue reconciliations. Your accountant's checklist as a widget." },
  { href: "/accounting/bulk-upload", label: "Bulk Upload", icon: Upload,
    desc: "Migrate from another system in one afternoon. CoA, vendors, bills, statements, invoices." },
];

export default function AccountingModule() {
  const location = useLocation();
  const isHubPage = location.pathname === "/accounting" || location.pathname === "/accounting/";

  return (
    <div className="space-y-6 animate-in" data-testid="accounting-module">
      {/* Module header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-gradient-to-br from-emerald-500/20 to-blue-500/20">
            <Sparkles className="h-7 w-7 text-emerald-600" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
              Accounting Pro
              <Badge className="bg-emerald-600 hover:bg-emerald-700">CA</Badge>
              <Badge variant="outline" className="border-amber-500/50 text-amber-700 dark:text-amber-300">Under Construction</Badge>
            </h1>
            <p className="text-muted-foreground max-w-2xl">
              Full double-entry accounting bundled with Live AR + Live AP.
              Canada-focused (GST/HST/PST), franchise multi-store books, and everything
              you need to migrate from QuickBooks / Xero / Wave in one afternoon.
            </p>
          </div>
        </div>
      </div>

      {/* Hub layout — big banner + sub-nav grid */}
      {isHubPage ? (
        <>
          <UnderConstructionBanner
            title="Accounting Pro (Canada) — Under Construction"
            subtitle="We're building this module step-by-step. Every sub-page has a shell in place so you can see the full scope. Purchasing the add-on now locks in launch pricing and auto-migrates your existing invoices + payables when each subsystem goes live."
            progress={8}
            nextMilestone="Chart of Accounts CRUD (Canadian standard template) + Journal Entries with auto-post toggle"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {SUBSECTIONS.map((s) => (
              <NavLink
                key={s.href}
                to={s.href}
                className="group"
                data-testid={`acct-nav-${s.href.replace(/\//g, '-')}`}
              >
                <Card className="h-full border-2 hover:border-primary/60 hover:bg-muted/30 transition-all cursor-pointer">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <div className="p-1.5 rounded-md bg-primary/10 group-hover:bg-primary/20 transition-colors">
                        <s.icon className="h-4 w-4 text-primary" />
                      </div>
                      {s.label}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-muted-foreground">{s.desc}</p>
                  </CardContent>
                </Card>
              </NavLink>
            ))}
          </div>
        </>
      ) : (
        // Sub-page layout — sidebar sub-nav + outlet for the actual sub-page content
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
          <nav className="space-y-1" data-testid="acct-subnav">
            {SUBSECTIONS.map((s) => (
              <NavLink
                key={s.href}
                to={s.href}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-foreground/80 hover:bg-muted hover:text-foreground"
                  }`
                }
              >
                <s.icon className="h-4 w-4" />
                {s.label}
              </NavLink>
            ))}
          </nav>
          <div>
            <Outlet />
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Sub-page shells — one per accounting subsystem                      */
/* ------------------------------------------------------------------ */

const _sub = (dataTestId, title, subtitle, nextMilestone) => (
  <div data-testid={dataTestId} className="space-y-4">
    <UnderConstructionBanner title={title} subtitle={subtitle} nextMilestone={nextMilestone} />
  </div>
);

export const AcctChartOfAccounts     = () => _sub("acct-page-coa",
  "Chart of Accounts — Under Construction",
  "You'll be able to view / add / edit / archive your ledger accounts. The Canadian standard small-business template will seed on first use (Cash, AR, GST/HST Payable, Wages, Rent, etc.).",
  "Wire the standard-template seed + CRUD API");

export const AcctJournalEntries      = () => _sub("acct-page-je",
  "Journal Entries — Under Construction",
  "Draft, review, and post double-entry journal entries. You'll get a per-tenant 'auto-post' toggle — when off, entries live as drafts until you review them. Default: OFF (safer for new tenants).",
  "Draft-JE editor with debit/credit balance check + auto-post toggle");

export const AcctGeneralLedger       = () => _sub("acct-page-gl",
  "General Ledger — Under Construction",
  "Drill into any account: running balance, all posted transactions, sub-ledger breakdown by client / vendor / branch.",
  "Ledger view for a single account with date-range filter");

export const AcctFinancialReports    = () => _sub("acct-page-reports",
  "Financial Reports — Under Construction",
  "Trial Balance · Profit & Loss · Balance Sheet · Sub-ledger reports. Date-range filtered. Exportable to PDF and CSV.",
  "Trial Balance report (the simplest one) as first working report");

export const AcctVendors             = () => _sub("acct-page-vendors",
  "Vendors & Suppliers — Under Construction",
  "Full supplier records: multiple contacts, addresses, terms (Net 15/30/60/COD), preferred payment method (EFT/cheque/credit), default GL account, tax IDs (GST/HST #, WCB #).",
  "Vendor CRUD + import from existing base-plan Payables");

export const AcctVendorBills         = () => _sub("acct-page-bills",
  "Vendor Bills (AP) — Under Construction",
  "Enter bills against a vendor: due date auto-fills from vendor terms, line items link to GL accounts, tax handled per line (GST/HST/PST/exempt).",
  "Bill creation form + GL account picker");

export const AcctCheques             = () => _sub("acct-page-cheques",
  "Cheque Printing — Under Construction",
  "Two-stub cheque layout: each stub lists every vendor invoice being paid + line-by-line running total = cheque amount. Compatible with pre-printed Canadian cheque stock.",
  "Cheque template renderer + batch-pay from vendor bill list");

export const AcctBankAccounts        = () => _sub("acct-page-banks",
  "Bank Accounts — Under Construction",
  "Multiple bank accounts per business, per branch, per franchise store. Chequing / Savings / Money Market / Credit Card. Each account links to a GL Cash account.",
  "Bank account CRUD + GL account linkage");

export const AcctBankReconciliation  = () => _sub("acct-page-recon",
  "Bank Reconciliation — Under Construction",
  "Import statements from RBC, TD, Scotia, BMO, CIBC, National Bank, Desjardins, HSBC-CA, Tangerine, Simplii (CSV/OFX/QBO). Auto-match to invoices/payments; manually confirm the rest.",
  "CSV upload + auto-match engine for RBC & TD (first two banks)");

export const AcctBranches            = () => _sub("acct-page-branches",
  "Franchise & Branches — Under Construction",
  "Head office + multi-store books. Each branch has its own books but rolls up to consolidated head-office reports. Supports same-owner and different-owner branches. Simplified core default + advanced customization.",
  "Branch CRUD + inter-branch chart-of-accounts inheritance rules");

export const AcctBulkUpload          = () => _sub("acct-page-bulk",
  "Bulk Upload / Migration — Under Construction",
  "Drop CSV/XLSX exports from QuickBooks/Xero/Wave/Sage. We map columns automatically, show you a preview, and import: Chart of Accounts, Vendors, Bills, Bank Transactions, historical Invoices, Journal Entries.",
  "CoA CSV parser + preview UI as first supported type");

export const AcctHealthScore         = () => _sub("acct-page-health",
  "Accounting Health Score — Under Construction",
  "Live gauge on your Business Settings + Accounting hub. Flags: unbalanced journal entries, unmatched bank transactions, overdue reconciliations, aged AR beyond terms, aged AP beyond terms, missing GST/HST filings, GL codes not mapped on active items. Green = clean books, amber = attention needed, red = something's off — click the flag to jump straight to the fix.",
  "Score calculator (7 signals) + gauge widget on Business Settings");
